/**
 * Cart Drawer — Cafeshi
 * Features: reservation timer, free shipping bar, upsell carousel (drag),
 * AJAX qty/remove/upsell, self re-render via Section Rendering API.
 */
(function () {
  'use strict';

  const SECTION_ID = 'iurop-cart-drawer';
  const RESERVATION_KEY = 'iurop:cart-reservation-expiry';
  const RESERVATION_EXPIRED_KEY = 'iurop:cart-reservation-expired';

  const qs = (sel, root) => (root || document).querySelector(sel);
  const qsa = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const updateCartCountUI = (count) => {
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('visually-hidden', count === 0);
    });
    // The drawer header count lives in the drawer markup and is only refreshed by
    // a full refreshDrawer(); patchDrawer() (partial updates on qty/remove) leaves
    // it stale, so keep it in sync here too.
    qsa('[data-cart-drawer-count]').forEach((el) => {
      el.textContent = count;
    });
  };

  // Empty-cart suggestions: show only the whole rows that fit the drawer height
  // so there's no scroll. A generous pool is rendered server-side; we hide the
  // overflow here, adapting the visible count to the screen size.
  const fitEmptyGrid = () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer || !drawer.classList.contains('is-open')) return;
    const grid = qs('.cart-drawer__empty-grid', drawer);
    const panel = qs('.cart-drawer__panel', drawer);
    if (!grid || !panel) return;
    const cards = qsa(':scope > *', grid);
    if (cards.length < 2) return;
    cards.forEach((c) => { c.style.display = ''; });
    const rowGap = parseFloat(getComputedStyle(grid).rowGap) || 16;
    const cardH = cards[0].getBoundingClientRect().height;
    if (!cardH) return;
    const available = panel.getBoundingClientRect().bottom - grid.getBoundingClientRect().top - 8;
    const rowsFit = Math.max(1, Math.floor((available + rowGap) / (cardH + rowGap)));
    cards.forEach((c, i) => { if (i >= rowsFit * 2) c.style.display = 'none'; });
  };

  // Push a history entry when the drawer opens so the mobile "back" gesture
  // (and the browser back button) close it instead of navigating away.
  let drawerHistoryPushed = false;

  const openDrawer = () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    requestAnimationFrame(fitEmptyGrid);
    if (!drawerHistoryPushed) {
      history.pushState({ cartDrawerOpen: true }, '');
      drawerHistoryPushed = true;
    }
  };

  let fitTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(fitEmptyGrid, 150);
  });

  const closeDrawer = (opts) => {
    const fromPop = opts === true;
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    // If we closed via UI (not the back gesture), pop the entry we pushed so
    // history stays clean.
    if (drawerHistoryPushed && !fromPop) {
      drawerHistoryPushed = false;
      history.back();
    } else {
      drawerHistoryPushed = false;
    }
  };

  window.addEventListener('popstate', () => {
    const drawer = qs('[data-cart-drawer]');
    if (drawer && drawer.classList.contains('is-open')) {
      closeDrawer(true);
    }
  });

  // ------------------------------------------------------------------
  // Reservation timer
  // ------------------------------------------------------------------
  let reservationInterval = null;

  const formatMMSS = (seconds) => {
    if (seconds < 0) seconds = 0;
    const mm = Math.floor(seconds / 60);
    const ss = seconds % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };

  const initReservation = () => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    const enabled = drawer.dataset.reservationEnabled === 'true';
    const minutes = parseInt(drawer.dataset.reservationMinutes, 10) || 10;
    const timerEl = qs('[data-reservation-timer]');
    if (!enabled || !timerEl) return;

    if (reservationInterval) {
      clearInterval(reservationInterval);
      reservationInterval = null;
    }

    const reservationEl = qs('[data-reservation]');
    const expireBanner = () => {
      if (!reservationEl) return;
      const expired = (window.theme && window.theme.t && window.theme.t.cart && window.theme.t.cart.reservationExpired) || 'TIEMPO EXPIRADO, ULTIMA OPORTUNIDAD';
      reservationEl.classList.add('is-expired');
      reservationEl.innerHTML =
        '<span class="cart-drawer__reservation-text">' + expired + '</span>';
    };

    // If a previous timer already expired in this session, keep showing the
    // expired banner instead of restarting the countdown — until the cart
    // empties (which clears the flag).
    let alreadyExpired = false;
    try { alreadyExpired = localStorage.getItem(RESERVATION_EXPIRED_KEY) === '1'; } catch (_) {}
    if (alreadyExpired) {
      expireBanner();
      return;
    }

    // Get or create expiry timestamp in localStorage.
    let expiry = null;
    try {
      const stored = parseInt(localStorage.getItem(RESERVATION_KEY), 10);
      if (stored && stored > Date.now()) expiry = stored;
    } catch (_) {}
    if (!expiry) {
      expiry = Date.now() + minutes * 60 * 1000;
      try { localStorage.setItem(RESERVATION_KEY, String(expiry)); } catch (_) {}
    }

    const tick = () => {
      const remaining = Math.floor((expiry - Date.now()) / 1000);
      if (remaining <= 0) {
        expireBanner();
        clearInterval(reservationInterval);
        reservationInterval = null;
        try {
          localStorage.removeItem(RESERVATION_KEY);
          localStorage.setItem(RESERVATION_EXPIRED_KEY, '1');
        } catch (_) {}
        return;
      }
      timerEl.textContent = formatMMSS(remaining);
    };
    tick();
    reservationInterval = setInterval(tick, 1000);
  };

  // Reset reservation when cart empties.
  const maybeClearReservation = (cart) => {
    if (cart && cart.item_count === 0) {
      try {
        localStorage.removeItem(RESERVATION_KEY);
        localStorage.removeItem(RESERVATION_EXPIRED_KEY);
      } catch (_) {}
    }
  };

  // ------------------------------------------------------------------
  // Re-render the whole drawer from the server.
  // ------------------------------------------------------------------
  // ------------------------------------------------------------------
  // Delivery estimate — "Recibe tu pedido del X al Y de <mes>"
  // Window (min/max hours) is configurable via the section settings and read
  // from data attributes. Dates are computed live from the current time.
  // ------------------------------------------------------------------
  const DELIVERY_MONTHS = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ];

  const renderDelivery = () => {
    const el = qs('[data-delivery]');
    if (!el) return;
    const span = el.querySelector('[data-delivery-text]');
    if (!span) return;

    const minH = parseInt(el.dataset.minHours, 10) || 48;
    const maxH = parseInt(el.dataset.maxHours, 10) || 72;
    const prefix = (el.dataset.prefix || 'Recibe tu pedido').trim();

    const now = Date.now();
    const from = new Date(now + minH * 3600 * 1000);
    const to = new Date(now + maxH * 3600 * 1000);

    const fromMonth = DELIVERY_MONTHS[from.getMonth()];
    const toMonth = DELIVERY_MONTHS[to.getMonth()];

    let range;
    if (from.getMonth() === to.getMonth()) {
      range = `del ${from.getDate()} al ${to.getDate()} de ${toMonth}`;
    } else {
      range = `del ${from.getDate()} de ${fromMonth} al ${to.getDate()} de ${toMonth}`;
    }

    span.textContent = `${prefix} ${range}`;
  };

  const refreshDrawer = () => {
    return fetch(`/?sections=${SECTION_ID}`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        const html = data[SECTION_ID];
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('[data-cart-drawer]');
        const currentDrawer = qs('[data-cart-drawer]');
        if (newDrawer && currentDrawer) {
          const wasOpen = currentDrawer.classList.contains('is-open');
          currentDrawer.innerHTML = newDrawer.innerHTML;
          if (wasOpen) currentDrawer.classList.add('is-open');
          bindEvents();
          initReservation();
          renderDelivery();
          requestAnimationFrame(fitEmptyGrid);
        }
      })
      .catch((err) => console.error('[cart-drawer] refresh failed', err))
      .then(() => { try { emitPack(); } catch (e) { console.error('[cart-drawer] emitPack', e); } try { reconcileGifts(); } catch (e) { console.error('[cart-drawer] gifts', e); } });
  };

  // ------------------------------------------------------------------
  // Volume-pack discounts (site-wide). The drawer renders one hidden
  // [data-pack-data] node per active pack (server-side, with collection access);
  // we broadcast `cart:pack` with the full list so each PDP widget can pick the
  // pack its product belongs to. No network call here — the discount applies on
  // its own via the checkout URL (?discount=CODE,CODE) set server-side whenever a
  // pack reaches its minimum. (An earlier version fetched /discount on every
  // render, which hammered Shopify into 429s.)
  const emitPack = () => {
    const nodes = qsa('[data-pack-data]');
    if (!nodes.length) return;
    const packs = nodes.map((n) => ({
      id: n.dataset.packId || '',
      collection: n.dataset.packCollection || '',
      enabled: n.dataset.packEnabled === '1',
      count: parseInt(n.dataset.packCount, 10) || 0,
      target: parseInt(n.dataset.packTarget, 10) || 3,
      pct: parseInt(n.dataset.packPct, 10) || 0,
      qty2: parseInt(n.dataset.packQty2, 10) || 0,
      pct2: parseInt(n.dataset.packPct2, 10) || 0,
      left: parseInt(n.dataset.packLeft, 10) || 0,
      state: n.dataset.packState || 'offer',
      savings: n.dataset.packSavings || '',
      code: n.dataset.packCode || '',
    }));
    document.dispatchEvent(new CustomEvent('cart:pack', { detail: { packs } }));
  };

  // ------------------------------------------------------------------
  // Auto-gifts (replaces the EasyGift app). The drawer renders one hidden
  // [data-gift-data] node per active rule (snippets/iurop-cart-gifts); this
  // reconciler adds the gift line when the trigger is in the cart and removes
  // it when the trigger leaves. The 0€ price comes from a native automatic
  // BXGY discount — the theme never fakes prices. One mutation per pass: the
  // follow-up refresh re-renders the nodes and the next pass continues.
  // sessionStorage remembers per-rule state so a gift the customer removed by
  // hand is not pushed back (until the trigger leaves and re-enters).
  // ------------------------------------------------------------------
  const GIFT_KEYS = {
    added: 'iurop:gift-added',
    declined: 'iurop:gift-declined',
    failed: 'iurop:gift-failed',
  };
  let giftInFlight = false;

  const giftMem = (key) => {
    try { return JSON.parse(sessionStorage.getItem(key) || '{}'); } catch (_) { return {}; }
  };
  const giftSet = (key, id, val) => {
    try {
      const m = giftMem(key);
      if (val === null) delete m[id]; else m[id] = val;
      sessionStorage.setItem(key, JSON.stringify(m));
    } catch (_) {}
  };

  const afterGiftMutation = (cart) => {
    if (cart && typeof cart.item_count === 'number') {
      updateCartCountUI(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, skipDrawerRefresh: true, openDrawer: false } }));
    }
    return refreshDrawer();
  };

  const removeGiftLine = (variantId) =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const line = cart.items.find(
          (it) => String(it.variant_id) === String(variantId) && it.properties && it.properties._gift
        );
        if (!line) return cart;
        return postChange(line.key, 0);
      });

  const reconcileGifts = () => {
    if (giftInFlight) return;
    // Mystery gifts from the PDP selector whose product left the cart.
    const orphan = qs('[data-gift-orphan]');
    if (orphan && orphan.dataset.lineKey) {
      giftInFlight = true;
      postChange(orphan.dataset.lineKey, 0)
        .then(afterGiftMutation)
        .catch((err) => console.error('[cart-gifts] orphan remove failed', err))
        .then(() => { giftInFlight = false; });
      return;
    }
    const nodes = qsa('[data-gift-data]');
    if (!nodes.length) return;
    const added = giftMem(GIFT_KEYS.added);
    const declined = giftMem(GIFT_KEYS.declined);
    const failed = giftMem(GIFT_KEYS.failed);

    for (const n of nodes) {
      const id = n.dataset.giftId;
      const variant = n.dataset.giftVariant;
      const needed = n.dataset.giftNeeded === '1';
      const present = n.dataset.giftPresent === '1';

      if (!needed) {
        // Trigger left the cart: forget any per-rule memory and take back the
        // auto-added gift if it is still there.
        if (declined[id]) giftSet(GIFT_KEYS.declined, id, null);
        if (failed[id]) giftSet(GIFT_KEYS.failed, id, null);
        // Take the gift back whenever its _gift line is still there, even if
        // this session did not add it (the line only exists because of the rule).
        if (n.dataset.giftLine === '1') {
          giftInFlight = true;
          removeGiftLine(variant)
            .then((cart) => { giftSet(GIFT_KEYS.added, id, null); return afterGiftMutation(cart); })
            .catch((err) => console.error('[cart-gifts] remove failed', err))
            .then(() => { giftInFlight = false; });
          return;
        }
        continue;
      }

      if (present) continue;
      if (declined[id] || failed[id]) continue;
      if (added[id]) {
        // We added it earlier and now it's gone with the trigger still present
        // → the customer removed it on purpose. Don't fight them.
        giftSet(GIFT_KEYS.declined, id, '1');
        continue;
      }

      const payload = {
        id: parseInt(variant, 10),
        quantity: parseInt(n.dataset.giftQty, 10) || 1,
        properties: { _gift: n.dataset.giftMessage || 'Regalo' },
      };
      if (n.dataset.giftLock === '1') payload.properties._gift_lock = '1';

      giftInFlight = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
        .then(() => {
          giftSet(GIFT_KEYS.added, id, '1');
          return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        })
        .then(afterGiftMutation)
        .catch((err) => {
          // Sold out / not published → remember and stop retrying this session.
          console.warn('[cart-gifts] add failed', err);
          giftSet(GIFT_KEYS.failed, id, '1');
        })
        .then(() => { giftInFlight = false; });
      return;
    }
  };

  // ------------------------------------------------------------------
  // Cart AJAX
  // ------------------------------------------------------------------
  // ------------------------------------------------------------------
  // In-place updates (no full re-render) — keeps numbers from snapping.
  // Money formatting mirrors the EUR / es-ES rendering ("1.234,56€").
  // ------------------------------------------------------------------
  const money = (cents) => {
    const parts = (Math.round(cents) / 100).toFixed(2).split('.');
    const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${int},${parts[1]}€`;
  };

  const bump = (el) => {
    if (!el) return;
    el.classList.remove('is-bumped');
    void el.offsetWidth; // restart animation
    el.classList.add('is-bumped');
  };

  const setText = (el, value) => {
    if (!el) return;
    if (el.textContent.trim() !== value) {
      el.textContent = value;
      bump(el);
    }
  };

  // Apple-style number roll: the old value slides up & blurs out while the new
  // value rises from below into focus.
  const rollNumber = (el, value) => {
    if (!el) return;
    const oldVal = el.textContent.trim();
    if (oldVal === value) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.textContent = value;
      return;
    }
    el.classList.add('cart-roll');
    el.innerHTML =
      `<span class="cart-roll__old">${oldVal}</span>` +
      `<span class="cart-roll__new">${value}</span>`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => el.classList.add('is-rolling'));
    });
    window.setTimeout(() => {
      el.classList.remove('cart-roll', 'is-rolling');
      el.textContent = value;
    }, 620);
  };

  // Original total = current prices before cart discounts. Product compare-at
  // savings stay on each line, so the subtotal % OFF shows only the real cart
  // discount (matches checkout).
  const computeOriginalTotal = (cart) =>
    cart.items.reduce((total, item) => total + item.original_line_price, 0);

  const patchDrawer = (cart) => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;

    // Line quantities + line prices + line compare-at
    cart.items.forEach((item) => {
      const input = drawer.querySelector(`[data-qty-input][data-line-key="${item.key}"]`);
      if (!input) return;
      if (parseInt(input.value, 10) !== item.quantity) {
        input.value = item.quantity;
        bump(input.closest('.cart-drawer__qty') || input);
      }
      const li = input.closest('.cart-drawer__item');
      if (!li) return;
      const priceEl = li.querySelector('.cart-drawer__item-price');
      if (priceEl) setText(priceEl, money(item.final_line_price));
      const cmpEl = li.querySelector('.cart-drawer__item-compare');
      if (cmpEl) {
        const cmpUnit = parseInt(li.dataset.compareAt, 10) || 0;
        if (cmpUnit > item.final_price) {
          cmpEl.hidden = false;
          setText(cmpEl, money(cmpUnit * item.quantity));
        } else {
          cmpEl.hidden = true;
        }
      }
    });

    // Subtotal + savings (compare-at aware)
    const originalTotal = computeOriginalTotal(cart);
    rollNumber(drawer.querySelector('[data-cart-subtotal]'), money(cart.total_price));
    const discounted = originalTotal > cart.total_price;
    const cmp = drawer.querySelector('[data-cart-compare]');
    if (cmp) {
      cmp.hidden = !discounted;
      if (discounted) setText(cmp, money(originalTotal));
    }
    const save = drawer.querySelector('.cart-drawer__subtotal-save');
    if (save) {
      const pct = discounted
        ? Math.floor(((originalTotal - cart.total_price) / originalTotal) * 100)
        : 0;
      // Hide the badge when the saving rounds to <1% (e.g. a few € off a pricey item).
      save.hidden = pct < 1;
      if (pct >= 1) setText(save, `${pct}% OFF`);
    }

    // Free-shipping bar
    const threshold = parseInt(drawer.dataset.thresholdCents, 10) || 0;
    if (threshold > 0) {
      const fill = drawer.querySelector('.cart-drawer__shipping-fill');
      if (fill) fill.style.width = `${Math.min(100, Math.round((cart.total_price * 100) / threshold))}%`;
      const amt = drawer.querySelector('[data-shipping-amount]');
      if (amt) setText(amt, money(Math.max(0, threshold - cart.total_price)));
    }
  };

  // Decide whether a change needs a full re-render (structural) or can be
  // patched in place (just numbers).
  const needsFullRender = (cart) => {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer || cart.item_count === 0) return true;
    // Volume-pack carts re-render fully so the saving message, progress and
    // checkout discount URL stay accurate when quantities change.
    const anyPack = qsa('[data-pack-data]', drawer)
      .some((n) => (parseInt(n.dataset.packCount, 10) || 0) > 0);
    if (anyPack) return true;
    // Gift goal rows depend on the net subtotal (packs, gifts): re-render.
    if (qs('[data-goal-gift]', drawer)) return true;
    const threshold = parseInt(drawer.dataset.thresholdCents, 10) || 0;
    const wasReached = !!drawer.querySelector('.cart-drawer__shipping.is-reached');
    const nowReached = threshold > 0 && cart.total_price >= threshold;
    if (wasReached !== nowReached) return true;
    const hadCompare = !!drawer.querySelector('.cart-drawer__subtotal-compare');
    const hasDiscount = computeOriginalTotal(cart) > cart.total_price;
    if (hadCompare !== hasDiscount) return true;
    return false;
  };

  const postChange = (lineKey, newQty) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: newQty }),
    }).then((r) => r.json());

  const changeLineQty = (lineKey, newQty) =>
    postChange(lineKey, newQty).then((cart) => {
      updateCartCountUI(cart.item_count);
      maybeClearReservation(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, skipDrawerRefresh: true } }));
      if (needsFullRender(cart)) return refreshDrawer();
      patchDrawer(cart);
      return cart;
    });

  // Remove with a leave animation. The network request is fired IMMEDIATELY and
  // runs in parallel with the animation, so the perceived wait is max(anim, RTT)
  // instead of anim + RTT.
  const removeLine = (li, lineKey) => {
    if (!li) return changeLineQty(lineKey, 0);
    li.classList.add('is-removing');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const req = postChange(lineKey, 0);
    const anim = new Promise((res) => window.setTimeout(res, reduce ? 0 : 200));
    return Promise.all([req, anim]).then(([cart]) => {
      updateCartCountUI(cart.item_count);
      maybeClearReservation(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, skipDrawerRefresh: true } }));
      if (cart.item_count === 0 || needsFullRender(cart)) {
        return refreshDrawer();
      }
      li.remove();
      patchDrawer(cart);
      return cart;
    });
  };

  // Remove every line that belongs to the pack in one go.
  const removePack = (keys) => {
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    })
      .then((r) => r.json())
      .then((cart) => {
        updateCartCountUI(cart.item_count);
        maybeClearReservation(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, skipDrawerRefresh: true } }));
        return refreshDrawer();
      });
  };

  const addUpsell = (btn) => {
    if (btn.disabled) return;

    // Only the in-drawer upsells should pop the drawer open afterwards. The same
    // [data-upsell-add] button is reused on product cards across the site
    // (home product lists, collection, search, top-sales) where adding must be
    // silent — add to cart, update the count, but don't hijack the page with the
    // drawer. Captured before refreshDrawer() re-renders the drawer markup.
    const insideDrawer = !!btn.closest('[data-cart-drawer]');

    // Use the selected variant from the row's <select> if present.
    const row = btn.closest('[data-upsell-row]');
    const sel = row && row.querySelector('[data-upsell-variant]');
    const variantId = (sel && sel.value) || btn.dataset.variantId;
    if (!variantId) return;

    // Icon-only buttons must not have their content replaced with text.
    const isIcon = !!btn.querySelector('svg');
    const originalText = isIcon ? null : btn.textContent;
    const tCart = (window.theme && window.theme.t && window.theme.t.cart) || {};

    btn.disabled = true;
    btn.classList.add('is-loading');
    if (!isIcon) btn.textContent = tCart.adding || 'Añadiendo…';

    const addPayload = { id: parseInt(variantId, 10), quantity: 1 };
    if (btn.dataset.sellingPlan) {
      addPayload.selling_plan = parseInt(btn.dataset.sellingPlan, 10);
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(addPayload),
    })
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
      .then(() => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()))
      .then((cart) => {
        updateCartCountUI(cart.item_count);
        btn.classList.remove('is-loading');
        if (!isIcon) btn.textContent = tCart.added || 'Añadido';
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, skipDrawerRefresh: true } }));
        // Briefly confirm with "Añadido", then restore the button so the card
        // stays reusable. refreshDrawer() only re-renders the cart section, so a
        // product-card button outside it (collection/search/top-sales) would
        // otherwise latch on "Añadido" forever — the PDP add-to-cart reverts the
        // same way.
        window.setTimeout(() => {
          btn.disabled = false;
          if (!isIcon) btn.textContent = originalText;
        }, 1600);
        // Refresh the drawer contents. Only re-open it for genuine in-drawer
        // upsells; adds from product cards stay silent (see insideDrawer above).
        return refreshDrawer().then(() => { if (insideDrawer) openDrawer(); });
      })
      .catch((err) => {
        console.error('[cart-drawer] add upsell failed', err);
        btn.disabled = false;
        btn.classList.remove('is-loading');
        if (!isIcon) btn.textContent = originalText;
      });
  };

  // Update an upsell card's price (and the add button's variant) when its
  // variant <select> changes.
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-upsell-variant]');
    if (!sel) return;
    const row = sel.closest('[data-upsell-row]');
    if (!row) return;
    const opt = sel.options[sel.selectedIndex];
    const priceEl = row.querySelector('[data-upsell-price]');
    const compareEl = row.querySelector('[data-upsell-compare]');
    const addBtn = row.querySelector('[data-upsell-add]');
    if (priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    if (compareEl) {
      if (opt.dataset.compare) {
        compareEl.textContent = opt.dataset.compare;
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (addBtn) {
      addBtn.dataset.variantId = sel.value;
      addBtn.disabled = opt.disabled;
    }
  });

  // ------------------------------------------------------------------
  // Upsell carousel — drag + progress sync
  // ------------------------------------------------------------------
  const initUpsellCarousel = () => {
    const track = qs('[data-upsells-track]');
    if (!track) return;

    const segments = qsa('.cart-drawer__upsells-progress-segment');
    const cards = qsa('[data-upsell-row]', track);

    // Drag support (pointer events)
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;

    track.addEventListener('pointerdown', (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScrollLeft = track.scrollLeft;
    });
    track.addEventListener('pointermove', (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        track.classList.add('is-dragging');
        // Capture only after a real drag begins, otherwise pointer capture
        // redirects the subsequent `click` event away from the button and
        // breaks the upsell "Añadir" handler.
        try { track.setPointerCapture(e.pointerId); } catch (_) {}
      }
      if (moved) track.scrollLeft = startScrollLeft - dx;
    });
    const endDrag = (e) => {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
      if (moved) {
        try { track.releasePointerCapture(e.pointerId); } catch (_) {}
      }
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);
    track.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);

    // Progress bar sync — IntersectionObserver per card
    if (segments.length && cards.length && 'IntersectionObserver' in window) {
      const setActive = (idx) => {
        segments.forEach((s, i) => s.classList.toggle('is-active', i === idx));
      };
      const observer = new IntersectionObserver(
        (entries) => {
          let best = null;
          entries.forEach((entry) => {
            if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
          });
          if (best && best.isIntersecting) {
            const idx = cards.indexOf(best.target);
            if (idx >= 0) setActive(idx);
          }
        },
        { root: track, threshold: [0.5, 0.75, 1] }
      );
      cards.forEach((c) => observer.observe(c));
    }
  };

  // ------------------------------------------------------------------
  // Bind events after each render
  // ------------------------------------------------------------------
  const bindEvents = () => {
    qsa('[data-cart-close]').forEach((el) => el.addEventListener('click', closeDrawer));

    qsa('[data-qty-control]').forEach((ctrl) => {
      const input = ctrl.querySelector('[data-qty-input]');
      if (!input) return;
      ctrl.querySelectorAll('[data-qty-change]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = parseInt(btn.dataset.qtyChange, 10);
          const newVal = Math.max(0, parseInt(input.value, 10) + delta);
          input.value = newVal;
          // Hitting 0 is a removal — route through removeLine so the row leaves
          // the DOM right away (changeLineQty/patchDrawer only touch surviving
          // lines, leaving the 0'd row behind until a reload).
          if (newVal === 0) {
            removeLine(input.closest('.cart-drawer__item'), input.dataset.lineKey);
          } else {
            changeLineQty(input.dataset.lineKey, newVal);
          }
        });
      });
      input.addEventListener('change', () => {
        const val = Math.max(0, parseInt(input.value, 10) || 0);
        if (val === 0) {
          removeLine(input.closest('.cart-drawer__item'), input.dataset.lineKey);
        } else {
          changeLineQty(input.dataset.lineKey, val);
        }
      });
    });

    qsa('[data-cart-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        btn.disabled = true;
        const row = btn.closest('.cart-drawer__item');
        removeLine(row, btn.dataset.lineKey);
      });
    });

    qsa('[data-cart-remove-pack]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const keys = (btn.dataset.packKeys || '').split(',').filter(Boolean);
        if (!keys.length) return;
        btn.disabled = true;
        removePack(keys);
      });
    });

    initUpsellCarousel();
  };

  // Delegated handler for upsell "Añadir" — survives drawer re-renders.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    addUpsell(btn);
  });

  // ------------------------------------------------------------------
  // Global init
  // ------------------------------------------------------------------
  const init = () => {
    bindEvents();
    initReservation();
    renderDelivery();
    try { emitPack(); } catch (e) { console.error('[cart-drawer] emitPack', e); } // initial coffee-pack state for the PDP widget
    try { reconcileGifts(); } catch (e) { console.error('[cart-drawer] gifts', e); } // initial auto-gift pass

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('#cart-icon-bubble, #cart-icon-bubble-mobile, [data-cart-open]');
      if (!trigger) return;
      e.preventDefault();
      openDrawer();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const drawer = qs('[data-cart-drawer]');
        if (drawer && drawer.classList.contains('is-open')) closeDrawer();
      }
    });

    document.addEventListener('cart:updated', (e) => {
      // The header count lives outside the drawer section, so refreshDrawer()
      // won't touch it — update it here whenever the event carries a cart.
      if (e.detail && e.detail.cart) updateCartCountUI(e.detail.cart.item_count);
      if (e.detail && e.detail.skipDrawerRefresh) return;
      refreshDrawer().then(() => {
        if (e.detail && e.detail.openDrawer !== false) openDrawer();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-cart-drawer]')) {
      bindEvents();
      initReservation();
      renderDelivery();
    }
  });
})();
